import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Plus,
  MapPin,
  Heart,
  Star,
  Calendar,
  Trash2,
  Home,
  Bookmark,
  Users,
  User,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlanStore } from '../store/planStore';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import FloatingSupport from '../components/FloatingSupport';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

export default function Saved() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { savedPlans, setSavedPlans, setCurrentPlan } = usePlanStore();
  
  const [savedIntents, setSavedIntents] = useState([]);
  const [activeTab, setActiveTab] = useState('plans');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    loadSavedData();
  }, [isAuthenticated, navigate]);
  
  const loadSavedData = async () => {
    try {
      setLoading(true);
      const [plansData, intentsData] = await Promise.all([
        api.getSavedPlans(user?.user_id || 'default').catch(() => ({ plans: [] })),
        api.getSavedIntents(user?.user_id || 'default').catch(() => ({ intents: [] })),
      ]);
      
      if (plansData.plans) setSavedPlans(plansData.plans);
      if (intentsData.intents) setSavedIntents(intentsData.intents);
    } catch (error) {
      console.error('Load saved data error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewPlan = (plan) => {
    setCurrentPlan(plan);
    navigate('/result');
  };
  
  const handleDeleteIntent = async (intentId) => {
    try {
      await api.deleteIntent(intentId);
      setSavedIntents(prev => prev.filter(i => i.id !== intentId));
      toast.success('Removed from saved');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="home-btn"
          >
            <Home size={20} />
          </Link>
          
          <div className="flex items-center gap-2">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Saved</span>
          </div>
          
          <Link 
            to="/planning"
            className="p-2 text-gray-400 hover:text-teal-500 hover:bg-teal-50 transition-colors"
            style={{ borderRadius: '50%' }}
            data-testid="add-plan-btn"
          >
            <Plus size={18} />
          </Link>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
        {/* Tabs */}
        <div className="flex items-center gap-2 p-1 bg-gray-100 mb-6" style={{ borderRadius: '9999px', width: 'fit-content' }}>
          {[
            { id: 'plans', label: 'Plans', icon: MapPin },
            { id: 'wishlist', label: 'Wishlist', icon: Heart },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2 font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-600 hover:text-teal-600'
              }`}
              style={{ borderRadius: '9999px' }}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-teal-500 mb-4" />
            <p className="text-gray-500">Loading your saved items...</p>
          </div>
        ) : (
          <>
            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <div className="space-y-4">
                {savedPlans.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 bg-teal-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1.5rem' }}>
                      <MapPin size={40} className="text-teal-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No saved plans yet</h3>
                    <p className="text-gray-500 mb-6">Start planning your first trip!</p>
                    <Link 
                      to="/planning"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all"
                      style={{ borderRadius: '9999px' }}
                      data-testid="create-plan-btn"
                    >
                      <Plus size={18} />
                      Create a Plan
                    </Link>
                  </motion.div>
                ) : (
                  savedPlans.map((plan, index) => (
                    <motion.div
                      key={plan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => handleViewPlan(plan)}
                      className="p-5 bg-white/80 backdrop-blur border border-gray-100 shadow hover:shadow-lg hover:border-teal-200 cursor-pointer transition-all"
                      style={{ borderRadius: '1.25rem' }}
                      data-testid={`plan-${plan.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                          <MapPin size={28} className="text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">{plan.destination}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {plan.duration_days} days
                            </span>
                            {plan.budget_breakdown?.total && (
                              <span className="font-medium text-teal-600">
                                ${plan.budget_breakdown.total.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1 bg-teal-50 text-teal-600 text-xs font-semibold" style={{ borderRadius: '9999px' }}>
                          <Star size={12} fill="currentColor" />
                          AI Plan
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
            
            {/* Wishlist Tab */}
            {activeTab === 'wishlist' && (
              <div className="space-y-4">
                {savedIntents.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 bg-rose-100 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '1.5rem' }}>
                      <Heart size={40} className="text-rose-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your wishlist is empty</h3>
                    <p className="text-gray-500 mb-6">{"Save destinations you'd love to visit!"}</p>
                    <Link 
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold hover:border-teal-300 transition-all"
                      style={{ borderRadius: '9999px' }}
                    >
                      Explore Destinations
                    </Link>
                  </motion.div>
                ) : (
                  savedIntents.map((intent, index) => (
                    <motion.div
                      key={intent.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-5 bg-white/80 backdrop-blur border border-gray-100 shadow hover:shadow-lg transition-all"
                      style={{ borderRadius: '1.25rem' }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center" style={{ borderRadius: '1rem' }}>
                          <Heart size={28} className="text-rose-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">{intent.destination || 'Dream Trip'}</h3>
              <p className="text-gray-500 text-sm mb-4">{intent.user_intent || 'Your dream trip awaits!'}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteIntent(intent.id);
                          }}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          style={{ borderRadius: '50%' }}
                          data-testid={`delete-${intent.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className="flex flex-col items-center gap-1 p-2 text-teal-600">
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className="flex flex-col items-center gap-1 p-2 text-gray-400">
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      <FloatingSupport />
    </div>
  );
}
